import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Mail, MessageSquare, Inbox, CheckCheck, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, EmptyState, Badge } from "./_ui";

const TYPE_META = {
  contact: { icon: Mail, color: "#FBBF24", bg: "rgba(245, 158, 11, 0.12)", label: "Contact site" },
  internal: { icon: MessageSquare, color: "#7AB0FF", bg: "rgba(0, 82, 204, 0.18)", label: "Message interne" },
};

export default function MessagerieAdminPanel() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/admin/inbox").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const markRead = async (cid) => {
    try {
      await api.put(`/admin/inbox/contact/${cid}/mark`);
      toast.success("Marqué comme lu");
      await load();
      if (selected && selected.id === cid) setSelected({ ...selected, status: "read" });
    } catch (e) { toast.error("Erreur"); }
  };

  if (!items) return (<><PageHeader title="Messagerie" /><Spinner /></>);

  const filtered = filter === "all"
    ? items
    : filter === "unread" ? items.filter((m) => m.status === "new")
    : items.filter((m) => m.type === filter);

  const newCount = items.filter((m) => m.status === "new").length;

  return (
    <div data-testid="admin-messagerie-admin">
      <PageHeader
        title="Messagerie"
        subtitle={`Inbox centralisée — ${items.length} message(s) total · ${newCount} non-lu(s)`}
      />

      <div className="flex flex-wrap gap-2 mb-5">
        {[
          { k: "all", label: `Tous (${items.length})` },
          { k: "unread", label: `Non lus (${newCount})` },
          { k: "contact", label: "Contact site" },
          { k: "internal", label: "Membres" },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
              filter === f.k
                ? "bg-[#D4AF37] text-[#0B1220] border-[#D4AF37]"
                : "bg-transparent text-slate-300 border-white/10 hover:border-[#D4AF37]/40"
            }`}
            data-testid={`inbox-filter-${f.k}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Inbox} title="Boîte vide" hint="Aucun message ne correspond à ce filtre." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* List */}
          <Card className="lg:col-span-5 max-h-[680px] overflow-y-auto admin-scroll">
            <div className="divide-y divide-white/5">
              {filtered.map((m) => {
                const meta = TYPE_META[m.type] || TYPE_META.internal;
                const Icon = meta.icon;
                const active = selected?.id === m.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => setSelected(m)}
                    className={`w-full text-left px-4 py-3.5 flex gap-3 transition-colors ${active ? "bg-white/[0.04]" : "hover:bg-white/[0.02]"}`}
                    data-testid={`inbox-item-${m.id}`}
                  >
                    <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0"
                      style={{ background: meta.bg, color: meta.color }}>
                      <Icon size={15} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm text-white truncate">{m.from_name || "Anonyme"}</div>
                        {m.status === "new" && <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 shrink-0" />}
                      </div>
                      <div className="text-xs text-slate-400 truncate mt-0.5">{m.subject}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {m.created_at ? new Date(m.created_at).toLocaleString("fr-FR") : ""}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Detail */}
          <Card className="lg:col-span-7 p-6">
            {!selected ? (
              <div className="h-full grid place-items-center text-sm text-slate-500 py-12">
                Sélectionnez un message pour le consulter
              </div>
            ) : (
              <div data-testid="inbox-detail">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge color="slate">{TYPE_META[selected.type]?.label || selected.type}</Badge>
                      {selected.status === "new" && <Badge color="gold">Nouveau</Badge>}
                    </div>
                    <h3 className="font-display text-xl text-white mt-2">{selected.subject}</h3>
                    <div className="text-xs text-slate-400 mt-1">
                      De <span className="text-white">{selected.from_name}</span>
                      {selected.from_email && <> · <a href={`mailto:${selected.from_email}`} className="text-[#7AB0FF] hover:underline inline-flex items-center gap-1">{selected.from_email}<ExternalLink size={10} /></a></>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Reçu le {selected.created_at ? new Date(selected.created_at).toLocaleString("fr-FR") : "—"}
                    </div>
                  </div>
                  {selected.type === "contact" && selected.status === "new" && (
                    <button
                      onClick={() => markRead(selected.id)}
                      className="text-xs text-emerald-400 hover:underline inline-flex items-center gap-1.5"
                      data-testid="inbox-mark-read"
                    >
                      <CheckCheck size={13} /> Marquer comme lu
                    </button>
                  )}
                </div>
                <div className="mt-6 p-5 rounded-lg bg-[#0B1220] border border-white/5 text-sm text-slate-200 whitespace-pre-line">
                  {selected.body}
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
